// src/bridge.ts
function sendToSW(message) {
  navigator.serviceWorker.controller?.postMessage(message);
}
function requestFromSW(message, expectedType, timeoutMs = 5e3) {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    function cleanup() {
      clearTimeout(timer);
      channel.port1.onmessage = null;
      channel.port1.close();
    }
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`DrySW request timeout: ${message.type}`));
    }, timeoutMs);
    channel.port1.onmessage = (event) => {
      cleanup();
      if (event.data?.type === expectedType) {
        resolve(event.data);
      } else {
        reject(new Error(`Unexpected response type: ${event.data?.type}`));
      }
    };
    navigator.serviceWorker.controller?.postMessage(message, [channel.port2]);
  });
}
function onSWMessage(handler) {
  const listener = (event) => {
    if (event.data?.type) {
      handler(event.data);
    }
  };
  navigator.serviceWorker.addEventListener("message", listener);
  return () => navigator.serviceWorker.removeEventListener("message", listener);
}

// src/sw-client.ts
var idCounter = 0;
function createDrySWClient() {
  return {
    queueRequest(request) {
      sendToSW({
        type: "QUEUE_REQUEST",
        payload: {
          ...request,
          id: `sw_${Date.now()}_${++idCounter}`,
          timestamp: Date.now()
        }
      });
    },
    syncPush(payload) {
      sendToSW({ type: "SYNC_PUSH", payload });
    },
    setTokens(tokens) {
      sendToSW({ type: "SET_TOKENS", payload: tokens });
    },
    clearTokens() {
      sendToSW({ type: "CLEAR_TOKENS" });
    },
    flushQueue() {
      sendToSW({ type: "FLUSH_QUEUE" });
    },
    clearQueue() {
      sendToSW({ type: "CLEAR_QUEUE" });
    },
    async getStatus() {
      const response = await requestFromSW({ type: "GET_STATUS" }, "STATUS");
      return response.payload;
    },
    onMessage(handler) {
      return onSWMessage(handler);
    },
    createQueueInterceptor() {
      return (error) => {
        if (error.isNetworkError || error.isServerError || error.isTimeout) {
          const config = error.config;
          if (config && config.method !== "GET") {
            this.queueRequest({
              url: config.url,
              method: config.method ?? "POST",
              headers: config.headers,
              body: typeof config.body === "string" ? config.body : JSON.stringify(config.body)
            });
          }
        }
        return error;
      };
    },
    createAuthSync() {
      return (_status, tokens) => {
        if (tokens?.accessToken) {
          this.setTokens(tokens);
        } else {
          this.clearTokens();
        }
      };
    },
    /**
     * Install visibility change listener that triggers queue replay on all browsers.
     * Call once on app startup. Returns cleanup function.
     */
    installVisibilitySync() {
      const handler = () => {
        if (document.visibilityState === "visible") {
          sendToSW({ type: "FLUSH_QUEUE" });
        }
      };
      document.addEventListener("visibilitychange", handler);
      const pageShowHandler = (event) => {
        if (event.persisted) {
          sendToSW({ type: "FLUSH_QUEUE" });
        }
      };
      window.addEventListener("pageshow", pageShowHandler);
      return () => {
        document.removeEventListener("visibilitychange", handler);
        window.removeEventListener("pageshow", pageShowHandler);
      };
    }
  };
}

// src/leader-election.ts
function createLeaderElection(channelName = "drystuff-leader") {
  const id = typeof crypto !== "undefined" && crypto.randomUUID ? `tab_${crypto.randomUUID()}` : `tab_${Date.now()}_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  let isLeader = false;
  let channel = null;
  let heartbeatTimer = null;
  let electionTimer = null;
  let started = false;
  const leaderCallbacks = /* @__PURE__ */ new Set();
  const followerCallbacks = /* @__PURE__ */ new Set();
  const HEARTBEAT_INTERVAL = 2e3;
  const ELECTION_TIMEOUT = 4e3;
  function broadcast(msg) {
    try {
      channel?.postMessage(msg);
    } catch {
    }
  }
  function becomeLeader() {
    if (isLeader) return;
    isLeader = true;
    broadcast({ type: "claim", id, timestamp: Date.now() });
    heartbeatTimer = setInterval(() => {
      broadcast({ type: "heartbeat", id, timestamp: Date.now() });
    }, HEARTBEAT_INTERVAL);
    for (const fn of leaderCallbacks) {
      try {
        fn();
      } catch {
      }
    }
  }
  function becomeFollower() {
    if (!isLeader) return;
    isLeader = false;
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    for (const fn of followerCallbacks) {
      try {
        fn();
      } catch {
      }
    }
  }
  function scheduleElection() {
    if (electionTimer) clearTimeout(electionTimer);
    electionTimer = setTimeout(() => {
      becomeLeader();
    }, ELECTION_TIMEOUT + Math.random() * 1e3);
  }
  function onMessage(event) {
    const msg = event.data;
    if (!msg?.type || msg.id === id) return;
    switch (msg.type) {
      case "heartbeat":
        msg.timestamp;
        if (isLeader) {
          if (msg.id < id) {
            becomeFollower();
          }
        }
        scheduleElection();
        break;
      case "claim":
        if (isLeader && msg.id < id) {
          becomeFollower();
        }
        msg.timestamp;
        scheduleElection();
        break;
      case "abdicate":
        scheduleElection();
        break;
    }
  }
  return {
    get isLeader() {
      return isLeader;
    },
    start() {
      if (started) return;
      started = true;
      if (typeof BroadcastChannel === "undefined") {
        becomeLeader();
        return;
      }
      channel = new BroadcastChannel(channelName);
      channel.onmessage = onMessage;
      scheduleElection();
    },
    stop() {
      if (!started) return;
      started = false;
      if (isLeader) {
        broadcast({ type: "abdicate", id, timestamp: Date.now() });
        becomeFollower();
      }
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (electionTimer) {
        clearTimeout(electionTimer);
        electionTimer = null;
      }
      channel?.close();
      channel = null;
    },
    onLeader(fn) {
      leaderCallbacks.add(fn);
      if (isLeader) fn();
      return () => leaderCallbacks.delete(fn);
    },
    onFollower(fn) {
      followerCallbacks.add(fn);
      if (!isLeader && started) fn();
      return () => followerCallbacks.delete(fn);
    },
    dispose() {
      this.stop();
      leaderCallbacks.clear();
      followerCallbacks.clear();
    }
  };
}

// src/tab-sync.ts
function createTabSync(channelName = "drystuff-tabsync") {
  if (typeof BroadcastChannel === "undefined") {
    return {
      request: async () => null,
      broadcast: () => {
      },
      onRequest: () => () => {
      },
      onBroadcast: () => () => {
      },
      dispose: () => {
      }
    };
  }
  const channel = new BroadcastChannel(channelName);
  const requestHandlers = /* @__PURE__ */ new Map();
  const broadcastHandlers = /* @__PURE__ */ new Map();
  const pendingRequests = /* @__PURE__ */ new Map();
  channel.onmessage = (event) => {
    const msg = event.data;
    if (!msg?.type) return;
    switch (msg.type) {
      case "request": {
        const handler = requestHandlers.get(msg.key);
        if (handler) {
          const result = handler();
          const respond = (state) => {
            channel.postMessage({
              type: "response",
              key: msg.key,
              requestId: msg.requestId,
              state
            });
          };
          if (result instanceof Promise) {
            result.then(respond);
          } else {
            respond(result);
          }
        }
        break;
      }
      case "response": {
        const resolve = msg.requestId ? pendingRequests.get(msg.requestId) : void 0;
        if (resolve) {
          pendingRequests.delete(msg.requestId);
          resolve(msg.state);
        }
        break;
      }
      case "broadcast": {
        const handlers = broadcastHandlers.get(msg.key);
        if (handlers) {
          for (const fn of handlers) {
            try {
              fn(msg.state);
            } catch {
            }
          }
        }
        break;
      }
    }
  };
  return {
    request(key, timeoutMs = 2e3) {
      return new Promise((resolve) => {
        const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const timer = setTimeout(() => {
          pendingRequests.delete(requestId);
          resolve(null);
        }, timeoutMs);
        pendingRequests.set(requestId, (state) => {
          clearTimeout(timer);
          resolve(state);
        });
        channel.postMessage({ type: "request", key, requestId });
      });
    },
    broadcast(key, state) {
      channel.postMessage({ type: "broadcast", key, state });
    },
    onRequest(key, handler) {
      requestHandlers.set(key, handler);
      return () => requestHandlers.delete(key);
    },
    onBroadcast(key, handler) {
      if (!broadcastHandlers.has(key)) broadcastHandlers.set(key, /* @__PURE__ */ new Set());
      broadcastHandlers.get(key).add(handler);
      return () => broadcastHandlers.get(key)?.delete(handler);
    },
    dispose() {
      channel.close();
      requestHandlers.clear();
      broadcastHandlers.clear();
      pendingRequests.clear();
    }
  };
}

export { createDrySWClient, createLeaderElection, createTabSync, onSWMessage, requestFromSW, sendToSW };
//# sourceMappingURL=client.mjs.map
//# sourceMappingURL=client.mjs.map